"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.MIMETypeSniffer = void 0;
/* eslint-disable no-multi-spaces */
const types_1 = require("./types");
/**
 * A MIME type sniffer that tries to determine the true type of the file by
 * content sniffing. It loads the first couple of bytes of the file and tries
 * to match them against known file signature bytes.
 *
 * The file signature bytes were taken from multiple sources, such as:
 * https://en.wikipedia.org/wiki/List_of_file_signatures
 * https://www.garykessler.net/library/file_sigs.html
 * https://gist.github.com/Qti3e/6341245314bf3513abb080677cd1c93b
 */
// prettier-ignore
const fileSignatureMap = new Map([
    // audio
    [/^fff1/, [types_1.DropzoneMIMEType.Aac]],
    [/^fff9/, [types_1.DropzoneMIMEType.Aac]],
    [/^fffb/, [types_1.DropzoneMIMEType.Mp3]],
    [/^fff3/, [types_1.DropzoneMIMEType.Mp3]],
    [/^fff2/, [types_1.DropzoneMIMEType.Mp3]],
    [/^494433/, [types_1.DropzoneMIMEType.Mp3]],
    [/^(.){8}667479704d344120/, [types_1.DropzoneMIMEType.M4a, types_1.DropzoneMIMEType.Mp4a]],
    [/^4f676753/, [types_1.DropzoneMIMEType.Ogg, types_1.DropzoneMIMEType.Ogv]],
    // images
    [/^424d/, [types_1.DropzoneMIMEType.Bmp]],
    [/^47494638/, [types_1.DropzoneMIMEType.Gif]],
    [/^ffd8/, [types_1.DropzoneMIMEType.Jpeg]],
    [/^89504e47/, [types_1.DropzoneMIMEType.Png]],
    [/^492049/, [types_1.DropzoneMIMEType.Tiff]],
    [/^49492a00/, [types_1.DropzoneMIMEType.Tiff]],
    [/^4d4d002a/, [types_1.DropzoneMIMEType.Tiff]],
    [/^4d4d002b/, [types_1.DropzoneMIMEType.Tiff]],
    [/^52494646/, [types_1.DropzoneMIMEType.Webp]],
    // video
    [/^00/, [types_1.DropzoneMIMEType.Mov]],
    [/^(.){8}6674797071742020/, [types_1.DropzoneMIMEType.Mov]],
    [/^(.){8}706e6f74/, [types_1.DropzoneMIMEType.Mov]],
    [/^(.){8}6d6f6f76/, [types_1.DropzoneMIMEType.Mov]],
    [/^(.){8}66726565/, [types_1.DropzoneMIMEType.Mov]],
    [/^(.){8}6d646174/, [types_1.DropzoneMIMEType.Mov]],
    [/^(.){8}77696465/, [types_1.DropzoneMIMEType.Mov]],
    [/^(.){8}736b6970/, [types_1.DropzoneMIMEType.Mov]],
    [/^(.){8}667479704d534e56/, [types_1.DropzoneMIMEType.Mp4]],
    [/^(.){8}667479706d703432/, [types_1.DropzoneMIMEType.Mp4]],
    [/^(.){8}6674797069736f6d/, [types_1.DropzoneMIMEType.Mp4]],
    [/^3026b2758e66cf11a6d900aa0062ce6c/, [types_1.DropzoneMIMEType.Wmv, types_1.DropzoneMIMEType.Wma]],
    [/^1a45dfa3/, [types_1.DropzoneMIMEType.Webm]],
    // pdf
    [/25504446/, [types_1.DropzoneMIMEType.Pdf]],
    // ms
    [/^0d444f43/, [types_1.DropzoneMIMEType.Doc]],
    [/^cf11e0a1b11ae100/, [types_1.DropzoneMIMEType.Doc]],
    [/^dba52d00/, [types_1.DropzoneMIMEType.Doc]],
    [/^d0cf11e0/, [types_1.DropzoneMIMEType.Doc, types_1.DropzoneMIMEType.Xls]],
    [/^504b03041/, [types_1.DropzoneMIMEType.Docx, types_1.DropzoneMIMEType.Xlsx]],
    // other
    [/^4449434d/, [types_1.DropzoneMIMEType.Dcm]],
]);
/**
 * The MIMETypeSniffer class is responsible for sniffing the MIME types of files.
 */
class MIMETypeSniffer {
    constructor(file) {
        this.file = file;
    }
    /**
     * Gets the first 1024 bytes of a file.
     * @param file - The file to get the bytes from.
     * @returns The first 1024 bytes of the file.
     */
    getBytes() {
        return __awaiter(this, void 0, void 0, function* () {
            const blob = this.file.slice(0, 1024);
            return new Uint8Array(yield blob.arrayBuffer());
        });
    }
    /**
     * Gets the header of a file as a hex string.
     * @returns The header of the file as a hex string.
     */
    getHeader() {
        return __awaiter(this, void 0, void 0, function* () {
            const bytes = yield this.getBytes();
            return bytes.reduce((hexBytes, byte) => hexBytes.concat(byte.toString(16).padStart(2, "0")), "");
        });
    }
    /**
     * Gets the MIME types of a file by sniffing its content.
     * @param file - The file to get the MIME types from.
     * @returns The MIME types of the file.
     */
    getMIMETypes() {
        return __awaiter(this, void 0, void 0, function* () {
            // Convert the bytes to a hex string
            const header = yield this.getHeader();
            // Match the file header with known file signatures
            const extensionsMatched = [];
            for (const [regexp, extensions] of fileSignatureMap) {
                if (regexp.test(header))
                    extensionsMatched.push(extensions);
            }
            return extensionsMatched.flat(1);
        });
    }
}
exports.MIMETypeSniffer = MIMETypeSniffer;
//# sourceMappingURL=MIMETypeSniffer.js.map